import { useState, useEffect } from 'react';
import { Bell, BellOff, Volume2, VolumeX } from 'lucide-react';
import { Label } from './ui/label';
import { Button } from './ui/button';
import {
  isNotificationSoundEnabled,
  setNotificationSoundEnabled,
  getNotificationVolume,
  setNotificationVolume,
  playNotificationSound
} from '../utils/notificationSound';

const NotificationSettings = () => {
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [volume, setVolume] = useState(0.5);

  useEffect(() => {
    setSoundEnabled(isNotificationSoundEnabled());
    setVolume(getNotificationVolume());
  }, []);

  const handleToggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    setNotificationSoundEnabled(newValue);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    setNotificationVolume(newVolume);
  };

  const handleTestSound = () => {
    // Temporarily enable sound for test
    const wasEnabled = isNotificationSoundEnabled();
    if (!wasEnabled) {
      setNotificationSoundEnabled(true);
    }
    playNotificationSound();
    if (!wasEnabled) {
      setTimeout(() => setNotificationSoundEnabled(false), 100);
    }
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-800">
      <h3 className="font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100">
        <Bell className="w-5 h-5" />
        Notification Settings
      </h3>
      
      {/* Sound Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {soundEnabled ? (
            <Volume2 className="w-4 h-4 text-green-600" />
          ) : (
            <VolumeX className="w-4 h-4 text-gray-400" />
          )}
          <Label className="text-sm">Notification Sound</Label>
        </div>
        <button
          onClick={handleToggleSound}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            soundEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              soundEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Volume Slider */}
      {soundEnabled && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Volume</Label>
            <span className="text-sm text-gray-500">{Math.round(volume * 100)}%</span>
          </div>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={handleVolumeChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
          />
        </div>
      )}

      {/* Test Sound Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={handleTestSound}
        className="w-full"
      >
        <Bell className="w-4 h-4 mr-2" />
        Test Notification Sound
      </Button>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        You&apos;ll hear this sound when someone mentions you in a chat room.
      </p>
    </div>
  );
};

export default NotificationSettings;
